import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';

export interface SecureDocumentShare {
  id: number;
  token: string;
  expires_at: string;
  existing?: boolean;
}

export interface SecureDocumentShareResponse {
  id: number;
  token: string;
  expires_at: string;
  existing: boolean;
}

export interface SecureDocumentShareExistsResponse {
  exists: boolean;
  id?: number;
  token?: string;
  expires_at?: string;
}

export interface SendEmailResponse {
  message: string;
  recipients: string[];
}

export interface ExpireShareResponse {
  message: string;
  expires_at: string;
}

export interface ViewDocumentResponse {
  document: {
    data: {
      link: string;
    };
  };
  document_name: string;
  company_name: string;
}

// Create a new secure document share
export const createSecureDocumentShare = async ({
  documentLink,
  companyId,
  expirationHours = 48,
  token,
}: APIRequest<{
  documentLink: string;
  companyId: number;
  expirationHours?: number;
}>): APIResponsePromise<SecureDocumentShareResponse> => {
  const requestData = {
    document_link: documentLink,
    company_id: companyId,
    expiration_hours: expirationHours,
  };

  try {
    const result = await _callBackend(
      '/secure_document_shares',
      'POST',
      requestData,
      token,
    );
    return result;
  } catch (error) {
    console.error('createSecureDocumentShare: API error:', error);
    throw error;
  }
};

// Check if there's an existing active share for a document
export const checkExistingSecureShare = async ({
  documentLink,
  companyId,
  token,
}: APIRequest<{
  documentLink: string;
  companyId: number;
}>): APIResponsePromise<SecureDocumentShareExistsResponse> => {
  const queryParams = new URLSearchParams({
    document_link: documentLink,
    company_id: companyId.toString(),
  });

  try {
    const result = await _callBackend(
      `/secure_document_shares/check_existing?${queryParams.toString()}`,
      'GET',
      null,
      token,
    );
    return result;
  } catch (error) {
    console.error('checkExistingSecureShare: API error:', error);
    throw error;
  }
};

// Send email with secure share link
export const sendSecureShareEmail = async ({
  secureShareId,
  companyId,
  emailAddresses,
  subject,
  message,
  token,
}: APIRequest<{
  secureShareId: number;
  companyId: number;
  emailAddresses: string[];
  subject?: string;
  message?: string;
}>): APIResponsePromise<SendEmailResponse> =>
  await _callBackend(
    `/secure_document_shares/${secureShareId}/send_email`,
    'POST',
    {
      company_id: companyId,
      email_addresses: emailAddresses,
      subject,
      message,
    },
    token,
  );

// Expire secure share link immediately
export const expireSecureShare = async ({
  secureShareId,
  companyId,
  token,
}: APIRequest<{
  secureShareId: number;
  companyId: number;
}>): APIResponsePromise<ExpireShareResponse> =>
  await _callBackend(
    `/secure_document_shares/${secureShareId}/expire`,
    'PATCH',
    {
      company_id: companyId,
    },
    token,
  );

// Public endpoint - view document via token (no auth required)
export const viewSharedDocument = async ({
  shareToken,
}: {
  shareToken: string;
}): APIResponsePromise<ViewDocumentResponse> =>
  await _callBackend(`/secure_document_shares/${shareToken}/view`, 'GET');

// Public endpoint - download document via token (no auth required)
export const downloadSharedDocument = async ({
  shareToken,
}: {
  shareToken: string;
}): APIResponsePromise<ViewDocumentResponse> =>
  await _callBackend(`/secure_document_shares/${shareToken}/download`, 'GET');
